import { AnyAction, Dispatch, MiddlewareAPI } from 'redux';

import {
  logChatbotConversationCreatedFromStartOver,
  logChatbotErrorMessageAppears,
  logChatbotSendMessage,
  logSearchResultsPresented,
  logSingleArticlePresented,
  logWidgetEmptySearchResults,
} from '../bi';
import { TRootState } from '../common/reducers';
import { chatActions } from '../common/reducers/chat';
import { errorsSlice } from '../common/reducers/errors';
import { fieldActions } from '../common/reducers/field';
import { ResetReason } from '../common/reducers/types';
import { selectEditorType } from '../common/selectors/homepage';
import { selectSolutions } from '../common/selectors/solutions';
import { loadGenerateAnswer } from '../common/thunks/answersGeneration.thunk';
import { loadArticleById, loadArticleByUri } from '../common/thunks/articles.thunk';
import { loadAttaches } from '../common/thunks/attaches.thunk';
import { loadSolutions } from '../common/thunks/solutions.thunk';
import { experiments } from '../experiments';
import { renderModel } from '../util/renderModel';

const isEditor = renderModel.isEditor();
const isStudioEditor = renderModel.isStudioEditor();
const locale = renderModel.getLocale();

export const biMiddleware = ({ getState }: MiddlewareAPI<any, TRootState>) => {
  const isDisplayAnswerGen = !isEditor && !isStudioEditor && locale === 'en' && experiments.is('specs.helpwidget.newHomePage', 'true');
  return (next: Dispatch) => (action: AnyAction) => {
    if (loadSolutions.fulfilled.match(action)) {
      const state = getState();
      if (!isDisplayAnswerGen) {
        logSearchResultsPresented(state, {
          articles: action.payload,
        });
      }
      if (state.solutions.isUserTypedSearch && action.payload.length === 0) {
        logWidgetEmptySearchResults(state);
      }
    } else if (chatActions.updateSession.match(action)) {
      const state = getState();
      const {
        chat: { resetReason, conversationId },
      } = state;

      if (resetReason === ResetReason.StartOver && action.payload.conversationId !== conversationId) {
        logChatbotConversationCreatedFromStartOver(state, {
          destinationConversationId: action.payload.conversationId,
        });
      }
    } else if (errorsSlice.actions.addError.match(action) || loadAttaches.rejected.match(action)) {
      if (action.payload?.bi) {
        logChatbotErrorMessageAppears(getState(), action.payload.bi);
      }
    } else if (fieldActions.sendMessage.match(action)) {
      logChatbotSendMessage(getState(), action.payload.bi);
    } else if (loadArticleById.fulfilled.match(action) || loadArticleByUri.fulfilled.match(action)) {
      // log article after it is set to state in reducers(some article fields are fetched from state)
      Promise.resolve().then(() => logSingleArticlePresented(getState(), action.payload));
    } else if (loadGenerateAnswer.fulfilled.match(action)) {
      const state = getState();
      const solutions = selectSolutions(state) || [];
      const editorType = selectEditorType(state);
      logSearchResultsPresented(state, {
        articles: solutions,
        generatedAnswer: {
          isLoading: false,
          error: null,
          query: action.payload.term || action.payload.query,
          isProhibited: action.payload.isProhibited,
          answer: action.payload.answer,
          usedArticles: action.payload.usedArticles,
        },
        editorType
      });
    } else if (loadGenerateAnswer.rejected.match(action)) {
      const state = getState();
      const solutions = selectSolutions(state) || [];
      logSearchResultsPresented(state, {
        articles: solutions.slice(0, 12),
        generatedAnswer: {
          ...state.answerGeneration,
          isLoading: false,
          error: action.error as string,
          answer: '',
        },
      });
    }

    return next(action);
  };
};
